<template>
  <section class="list-section">
    <slot name="table-banner" :total="total" />
    <TableControl
      v-if="!hideTableControl"
      ref="listControl"
      :is-async-data="isAsync"
      :disabled="isLoading || isCreationRowVisible"
      :total="total"
      :is-search-enabled="!!primaryNameField"
      @search-change="onSearchChange"
    >
      <template #custom-controls="props">
        <slot name="custom-controls" v-bind="props" :columns="dtColumns" />
        <section v-if="hasFilterableColumns && showFilterPopover" class="m-l-10">
          <TableFilterPopOver
            :columns="columns"
            :data-service="dataService"
            :table-id="tableId"
            :can-cache-params="canCacheParams"
            :filter-popover-position="filterPopoverPosition"
          />
        </section>
        <slot name="custom-controls-right-filter" v-bind="props" :columns="dtColumns" />
      </template>
      <template #table-actions="props">
        <slot name="custom-controls-right" v-bind="props" :columns="dtColumns" />
        <slot name="table-actions" v-bind="props"></slot>
      </template>
    </TableControl>
    <slot name="custom-sub-header" :dt-data="dtData" />
    <TableView
      ref="btable"
      :id="tableId"
      :data="dtData"
      :loading="isLoading"
      :paginated="isPaginatedAndIdle"
      :backend-pagination="isAsync"
      :backend-sorting="isAsync || isAsyncSorting"
      :custom-row-key="rowKey"
      :total="total"
      :icon-pack="iconPack"
      :sort-icon="sortIcon"
      :per-page="perPage"
      :checkable="checkable"
      :header-checkable="headerCheckable"
      :custom-is-checked="customIsChecked"
      :checked-rows="checkedRows"
      :is-row-checkable="isRowCheckable"
      :current-page="page"
      :hoverable="isHoverable"
      :default-sort-direction="defaultSortDirection"
      :default-sort="[sortField, sortOrder]"
      :sticky-header="stickyHeader"
      :height="tableHeight"
      :is-row-selectable="canSelectRow"
      :selected.sync="selected"
      :row-class="rowClass"
      :class="tableClass"
      :detailed="hasDetailed"
      :show-detail-icon="showDetailIcon"
      :show-header="displayTableHeader"
      :detail-key="detailKey"
      :mobile-cards="showMobileCards"
      detail-icon="cls cls-caret-right"
      @page-change="onPageChange"
      @sort="onSort"
      @click="onRowClick"
      @check="onCheckClick"
      @update:checkedRows="onCheckedRowsUpdate"
      @update:checked-rows="onCheckedRowsUpdate"
    >
      <slot
        name="table"
        :sort-order="sortOrder"
        :sort-field="sortField"
        :dt-data="dtData"
        :dt-columns="dtColumns"
        :render-service="renderService"
        :data-service="dataService"
      >
        <b-table-column
          v-for="column in dtColumns"
          v-bind="column"
          :key="dtKey(column)"
          :sortable="column.sortable"
          :custom-sort="column.customSort"
        >
          <template #default="props">
            <div v-if="column.renderHtml" v-dompurify-html="getValue(column, props.row)"></div>
            <template v-else-if="column.meta.cellRender.type === CellRenderType.BOOLEAN">
              <BooleanCell :cell-data="getValue(column, props.row)" />
            </template>
            <template v-else-if="column.meta.cellRender.type === CellRenderType.DOCUMENTS">
              <DocumentListCell
                v-if="props.row[column.field]"
                :cell-data="getValue(column, props.row)"
                :column="column"
              />
            </template>
            <template v-else-if="column.meta.cellRender.type === CellRenderType.ACTIONS">
              <section v-if="isActionColumnVisible" class="actions-cell has-text-right">
                <slot
                  v-if="!isLoadingRow(props.row)"
                  name="actions"
                  :table-id="tableId"
                  :cell-data="getValue(column, props.row)"
                  :row-data="props.row"
                  :column="column"
                  :dt-columns="{ ...dtColumns }"
                  :render-service="renderService"
                  :data-service="dataService"
                  :primary-key-field="primaryKeyField"
                  :primary-name-field="primaryNameField"
                  :is-creation-row-visible="isCreationRowVisible"
                >
                  <DefaultActionCell
                    :table-id="tableId"
                    :cell-data="getValue(column, props.row)"
                    :row-data="props.row"
                    :column="column"
                    :render-service="renderService"
                    :data-service="dataService"
                    :primary-key-field="primaryKeyField"
                    :primary-name-field="primaryNameField"
                  />
                </slot>
                <LoadingView v-else active :is-full-page="false" />
              </section>
            </template>
            <template
              v-else-if="
                column.meta.cellRender.type === CellRenderType.CUSTOM && $slots[column.field]
              "
            >
              <slot
                :name="column.field"
                :cell-data="getValue(column, props.row)"
                :row-data="props.row"
                :column="column"
                :table-id="tableId"
              />
            </template>
            <template v-else>
              <span>
                {{ getValue(column, props.row) }}
              </span>
            </template>
          </template>
          <template v-if="hasCustomHeader(column)" #header>
            <template v-if="column.meta.headerTooltipConfig">
              <div
                class="is-flex is-align-items-center"
                :data-e2e="TableCypressAttribute.views.TOOLTIP"
              >
                <span class="has-text-weight-semibold" :class="column.headerClass">
                  {{ column.label }}
                </span>
                <div
                  class="is-clickable column-tooltip"
                  @click.prevent.stop
                  :data-e2e="TableCypressAttribute.buttons.TOOLTIP"
                >
                  <TooltipView
                    type="is-dark"
                    :position="column.meta.headerTooltipConfig.position"
                    :size="column.meta.headerTooltipConfig.size"
                    :label="column.meta.headerTooltipConfig.label"
                    :triggers="column.meta.headerTooltipConfig.triggers"
                    multilined
                  >
                    <IconView pack="cls" size="is-small" custom-class="cls-information-circle" />
                  </TooltipView>
                </div>
                <IconView
                  v-if="column.sortable"
                  pack="cls"
                  size="is-small"
                  custom-class="cls-arrow-down"
                  :class="{
                    'is-desc': sortOrder === 'desc',
                    'is-invisible': sortField !== column.field
                  }"
                  :data-e2e="TableCypressAttribute.buttons.CUSTOM_HEADER_SORT_ICON"
                />
              </div>
            </template>
            <slot
              v-else
              :name="`${column.field}-header`"
              :column="column"
              :render-service="renderService"
              :data-service="dataService"
              :table-id="tableId"
            />
          </template>
        </b-table-column>
      </slot>
      <template #detail="props">
        <slot name="row-detail" v-bind="props" />
      </template>
      <template v-if="!isLoading && !hideEmptyState" #empty>
        <div v-if="isSearching || !$slots.empty" class="empty-state-container">
          <img src="@/assets/illustrations/search-empty.svg" alt="" />
          <div class="has-text-centered">{{ $t('core.no-results-found') }}</div>
        </div>
        <slot v-else name="empty" />
      </template>
      <template v-if="!isLoading && hasFooter" #footer>
        <th v-for="column in dtColumns" :key="column.field">
          <slot
            v-if="$slots[column.footerSlotName]"
            :name="column.footerSlotName"
            :table-id="tableId"
            :render-service="renderService"
            :dt-data="dtData"
            :dt-columns="dtColumns"
            :column="column"
            :data-service="dataService"
          />
        </th>
        <slot
          name="custom-footer"
          :table-id="tableId"
          :render-service="renderService"
          :dt-data="dtData"
          :dt-columns="dtColumns"
          :data-service="dataService"
        />
      </template>
    </TableView>
  </section>
</template>

<script>
import { decamelize } from 'humps';
import { assign, isEmpty } from 'lodash';
import TableControl from '/@/modules/core/table/components/TableControl.vue';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { TableEvent } from '/@/modules/core/table/constants/TableEvent';
import { CellRenderType, SortOrder } from '/@/modules/core/table/constants/TableConstant';
import { TableRenderService } from '/@/modules/core/table/services/TableRenderService';
import DefaultActionCell from '/@/modules/core/table/components/DefaultActionCell.vue';
import BooleanCell from '/@/modules/core/table/components/BooleanCell.vue';
import { BaseListService } from '/@/modules/core/table/services/BaseListService';
import { TableEmptyState } from '/@/modules/core/table/models/TableEmptyState';
import { BaseColumns } from '/@/modules/core/table/models/BaseColumns';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import DocumentListCell from '/@/modules/core/table/components/DocumentListCell.vue';
import TableFilterPopOver from '/@/modules/core/table/components/TableFilterPopOver.vue';
import { TableCypressAttribute } from '/@/modules/core/table/constants/TableCypressAttribute';
import Logger from '/@/modules/core/app/helpers/Logger';
import { TableDataSet } from '/@/modules/core/table/models/TableDataSet';
import { TableParamCache } from '/@/modules/core/table/services/TableParamCache';
import IconView from '@/modules/core/ui/components/wrappers/IconView.vue';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';
import TableView from '@/modules/core/ui/components/wrappers/TableView.vue';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'Table',
  emits: ['selected', 'on-checked'],
  slotsDefinition: {
    tableBanner: { total: Number },
    customControls: { columns: Object },
    customControlsRightFilter: { columns: Object },
    customControlsRight: { columns: Object },
    tableActions: {},
    customSubHeader: { dtData: Array },
    table: {
      sortOrder: String,
      sortField: String,
      dtData: Array,
      dtColumns: Array,
      renderService: TableRenderService,
      dataService: BaseListService
    },
    actions: {
      tableId: String,
      cellData: Object,
      rowData: Object,
      column: Object,
      dtColumns: Array,
      renderService: TableRenderService,
      dataService: BaseListService,
      primaryKeyField: String,
      primaryNameField: String,
      isCreationRowVisible: Boolean
    },
    rowDetail: { props: Object },
    empty: {},
    customFooter: {
      tableId: String,
      renderService: TableRenderService,
      dtData: Array,
      dtColumns: Array,
      dataService: BaseListService
    }
  },
  components: {
    TooltipView,
    IconView,
    TableFilterPopOver,
    DocumentListCell,
    TableControl,
    DefaultActionCell,
    BooleanCell,
    LoadingView,
    TableView
  },
  props: {
    hideTableControl: {
      type: Boolean,
      default: false
    },
    tableId: {
      type: String
    },
    tableClass: {
      type: String,
      default: () => ''
    },
    dataService: {
      type: BaseListService
    },
    predefinedData: {
      type: Array
    },
    predefinedCheckedRows: {
      type: Array
    },
    isSingleCheckRow: {
      type: Boolean,
      default: false
    },
    iconPack: {
      type: String,
      default: 'cls'
    },
    sortIcon: {
      type: String,
      default: 'cls-arrow-down'
    },
    columns: {
      type: BaseColumns,
      required: true
    },
    isAsync: {
      type: Boolean,
      default: false
    },
    isAsyncSorting: {
      type: Boolean,
      default: false
    },
    isHoverable: {
      type: Boolean,
      default: false
    },
    defaultPerPage: {
      type: Number,
      default: 10
    },
    queryParams: {
      type: Object,
      default: () => ({})
    },
    isPaginated: {
      type: Boolean,
      default: false
    },
    isFilterable: {
      type: Boolean,
      default: true
    },
    isRowSelectable: {
      type: Boolean,
      default: false
    },
    hasSiblingTables: {
      type: Boolean,
      default: false
    },
    displayTableHeader: {
      type: Boolean,
      default: true
    },
    emptyState: {
      type: TableEmptyState,
      default: null
    },
    hideEmptyState: {
      type: Boolean,
      default: false
    },
    isActionColumnVisible: {
      type: Boolean,
      default: false
    },
    stickyHeader: {
      type: Boolean,
      default: false
    },
    headerCheckable: {
      type: Boolean,
      default: true
    },
    checkable: {
      type: Boolean,
      default: false
    },
    height: {
      type: [Number, String],
      required: false,
      default: undefined
    },
    customRowKey: {
      type: String
    },
    customIsChecked: {
      Type: Function
    },
    hasDetailed: {
      type: Boolean,
      default: false
    },
    hideDetailIcon: {
      type: Boolean
    },
    detailKey: {
      type: String,
      default: 'id'
    },
    showMobileCards: {
      type: Boolean,
      default: true
    },
    canCacheParams: {
      type: Boolean,
      default: false
    },
    enableFuzzySearch: {
      type: Boolean,
      default: true
    },
    defaultSortOrder: {
      type: String,
      default: SortOrder.ASC
    },
    filterPopoverPosition: {
      type: String,
      default: 'is-bottom-right'
    },
    preSelectRow: {
      type: Object,
      default: null
    },
    showFilterPopover: {
      type: Boolean,
      default: true
    },
    customRowClassFn: {
      type: Function,
      default: () => ''
    }
  },
  static: {
    loadingRowClass: 'loading-row',
    TableCypressAttribute
  },
  events() {
    return {
      [TableEvent.RELOAD]: this.reloadTable,
      [TableEvent.RELOAD_AND_PERSIST_PAGE]: this.reloadAndPersistPage,
      [TableEvent.RELOAD_AND_SELECT]: this.reloadTableAndSelectRow,
      [TableEvent.SET_LOADING]: this.setLoading,
      [TableEvent.ADD_ROW]: this.addRow,
      [TableEvent.UPDATE_ROW]: this.updateRow,
      [TableEvent.DELETE_START_ROW]: this.deleteStartRow,
      [TableEvent.DELETE_ROW]: this.deleteRow,
      [TableEvent.REMOVE_SIBLING_SELECTED_ROWS]: this.removeSelectedSiblingRows,
      [TableEvent.REMOVE_SELECTED_ROW]: this.removeSelectedRow,
      [TableEvent.RESTORE_FAILED_ROW]: this.restoreFailedRow,
      [TableEvent.SELECT_ROW]: this.selectRow,
      [TableEvent.START_IS_LOADING_ROW]: this.startIsLoadingRow,
      [TableEvent.STOP_IS_LOADING_ROW]: this.stopIsLoadingRow,
      [TableEvent.SEARCH]: this.onSearchEvent,
      [TableEvent.TOGGLE_DETAILS]: this.toggleDetails,
      [TableEvent.HIDE_DETAILS]: this.hideDetails,
      [TableEvent.PREVIOUS_PAGE]: this.goToPreviousPage,
      [TableEvent.NEXT_PAGE]: this.goToNextPage,
      [TableEvent.ADD_CREATION_ROW]: this.addCreationRow,
      [TableEvent.DELETE_CREATION_ROW]: this.deleteCreationRow,
      [TableEvent.ON_HIGHLIGHT_ROWS_REQUESTED]: this.highlightRows
    };
  },
  data() {
    return {
      renderService: new TableRenderService(),
      CellRenderType,
      ColumnFormat,
      dtData: this.predefinedData || [],
      dtColumns: [],
      total: 0,
      search: null,
      page: 1,
      sortField: null,
      sortOrder: this.defaultSortOrder,
      defaultSortDirection: this.defaultSortOrder,
      perPage: this.defaultPerPage,
      needsRefresh: false,
      isLoading: false,
      isSearching: false,
      rowClass: this.customRowClassFn,
      tableHeight: this.height,
      selected: this.preSelectRow,
      checkedRows: this.predefinedCheckedRows ?? [],
      tableParamCache: new TableParamCache(this.tableId, this.columns),
      // Some Buefy flows emit both `check` and `update:checkedRows` for a single user interaction.
      // We use this flag to avoid emitting `on-checked` twice.
      receivedCheckInTick: false,
      isCreationRowVisible: false
    };
  },
  computed: {
    isEmpty() {
      return !this.dtData?.length && !this.isLoading;
    },
    primaryKeyField() {
      return this.renderService.getPrimaryKeyField(this.dtColumns);
    },
    primaryNameField() {
      return this.renderService.getPrimaryNameField(this.dtColumns);
    },
    hasFilterableColumns() {
      return !!this.dtColumns.find((column) => column.isFilterable);
    },
    rowKey() {
      return this.customRowKey ?? this.primaryKeyField;
    },
    isBackendPaginated() {
      return this.isPaginated && this.isAsync;
    },
    isPaginatedAndIdle() {
      return this.isPaginated && !this.isCreationRowVisible;
    },
    hasFooter() {
      return (
        this.$slots.customFooter ||
        this.dtColumns.find((column) => this.$slots[column.footerSlotName])
      );
    },
    showDetailIcon() {
      if (this.hideDetailIcon) {
        return false;
      }

      return this.hasDetailed;
    }
  },
  methods: {
    async loadData() {
      this.isLoading = true;

      const params = assign({}, this.queryParams);

      const sort = this.renderService.getSortField(this.sortOrder, this.sortField);
      if (sort) {
        params.sort = sort;
      }

      if (this.search) {
        const searchField = decamelize(this.primaryNameField);
        if (this.enableFuzzySearch) {
          params.like = `${searchField},${this.search}`;
        } else {
          params[searchField] = this.search;
        }
      } else {
        delete params.like;
      }

      if (this.isBackendPaginated) {
        params.page = `${this.renderService.getPage(this.page)},${this.perPage}`;
      }
      try {
        await this.processData(params);

        this.emitData();
        this.setLoading(this.tableId, false);
      } catch (e) {
        this.dtData = [];
        this.total = 0;
        this.setLoading(this.tableId, false);
        throw e;
      }
    },
    async processData(params = {}) {
      const fetchedData = this.predefinedData
        ? this.predefinedData
        : await this.dataService.getRows(params);

      this.dtColumns = this.renderService.preProcessColumns(this.columns);

      const dataSet = new TableDataSet(
        this.isBackendPaginated ? fetchedData.data : fetchedData,
        fetchedData.total
      );

      [this.dtData, this.dtColumns] = this.renderService.postProcessPayload(
        dataSet.data,
        this.dtColumns
      );

      this.total = this.isBackendPaginated ? dataSet.total : this.dtData.length;
    },
    dtKey({ field }) {
      return `${this.tableId}-${field}`;
    },
    resetRowClass() {
      this.rowClass = () => '';
    },
    onSort(field, order) {
      if (this.isAsync || this.isAsyncSorting) {
        const column = this.dtColumns.find((dtColumn) => dtColumn.field === field);
        const resolvedOrder = column?.invertSortDirection ? this.invertSortOrder(order) : order;

        this.sortField = field;
        this.sortOrder = resolvedOrder;
        this.tableParamCache.sortField = field;
        this.tableParamCache.sortOrder = resolvedOrder;
        this.page = 1;
        this.tableParamCache.page = 1;
        this.loadData();
      }
    },
    invertSortOrder(order) {
      return order === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC;
    },
    onPageChange(page) {
      this.clearCheckedRows();
      this.page = page;
      this.tableParamCache.page = page;

      if (this.isBackendPaginated) {
        this.loadData();
      }
    },
    goToPreviousPage() {
      if (this.page === 1) {
        return;
      }

      this.onPageChange(this.page - 1);
    },
    goToNextPage() {
      if (this.page * this.perPage < this.total) {
        this.onPageChange(this.page + 1);
      }
    },
    onPerPageChange(perPage) {
      this.perPage = perPage;

      if (this.isBackendPaginated) {
        this.loadData();
      }
    },
    onSearchChange(value) {
      this.isSearching = !isEmpty(value);
      if (this.isAsync) {
        this.search = value;
        if (this.search === '' || this.search.trim() !== '') {
          this.loadData();
        }
      } else {
        this.dtData = value.length
          ? this.renderService.searchData(value, this.dtColumns)
          : this.renderService.getOriginalData();
      }
    },
    onSearchEvent({ tableId, value }) {
      if (tableId === this.tableId) {
        this.onSearchChange(value);
        this.emitData();
      }
    },
    isLoadingRow(row) {
      const rowClassValue = this.rowClass(row);

      if (typeof rowClassValue === 'string') {
        return rowClassValue.includes(this.loadingRowClass);
      }

      return rowClassValue;
    },
    reloadTable(tableId) {
      if (this.isAsync) {
        this.page = 1;
      }

      this.stopIsLoadingRow({ tableId });
      if (this.tableId === tableId) {
        this.loadData();
      }
    },
    reloadAndPersistPage(tableId) {
      // For async tables, when we want to reload the table without resetting the page
      this.stopIsLoadingRow({ tableId });
      if (this.tableId === tableId) {
        this.loadData();
      }
    },
    async reloadTableAndSelectRow(tableId, row) {
      if (this.tableId === tableId) {
        await this.loadData();
        if (this.hasSiblingTables && this.canSelectRow()) {
          this.selected = this.dtData.find((data) => data.id === row.id);
          this.$emit('selected', this.selected);
        }
      }
    },
    setLoading(tableId, isLoading = true) {
      if (this.isLoading === isLoading) {
        return;
      }

      if (this.tableId === tableId) {
        this.isLoading = isLoading;
        EventBus.signal(
          TableEvent.LOADING_STATE_CHANGED.replace('{table_id}', this.tableId),
          isLoading
        );
      }
    },
    addRow({ tableId, row }) {
      this.handleRow(tableId, async () => {
        const newRow = await this.dataService.getRow(row[this.primaryKeyField]);
        this.dtData = this.renderService.postProcessAddRow(this.dtData, this.dtColumns, newRow);
        this.emitData();
      });
    },
    addCreationRow(tableId, row) {
      if (this.tableId !== tableId || !this.isAsync) {
        return;
      }

      const currentPageItemCount = this.dtData.length;
      const isCurrentPageFull = currentPageItemCount >= this.perPage;

      if (isCurrentPageFull) {
        this.perPage = this.perPage + 1;
      }

      this.dtData = this.renderService.postProcessAddRow(this.dtData, this.dtColumns, row);
      this.toggleDetails(tableId, row);
      this.isCreationRowVisible = true;
    },
    deleteCreationRow(tableId, row) {
      if (this.tableId !== tableId || !this.isAsync) {
        return;
      }

      if (this.perPage > this.defaultPerPage) {
        this.perPage = this.defaultPerPage;
      }

      this.dtData = this.renderService.postProcessDeleteRow(this.dtData, row);
      this.toggleDetails(tableId, row);
      this.isCreationRowVisible = false;
    },
    async updateRow({ tableId, row }) {
      if (this.tableId !== tableId) {
        return;
      }
      if (this.isAsync) {
        return this.reloadTable(this.tableId);
      }
      this.rowClass = (rowData, _) =>
        rowData[this.primaryKeyField] === row[this.primaryKeyField] && this.loadingRowClass;
      const newRow = await this.dataService.getRow(row[this.primaryKeyField], this.queryParams);
      this.emitRow(newRow);
      this.dtData = this.renderService.postProcessUpdateRow(
        this.dtData,
        this.dtColumns,
        row,
        newRow
      );
      this.resetRowClass();
      this.emitData();
      if (this.selected && this.canSelectRow()) {
        this.refreshSelectedRow(row, newRow);
      }
    },
    deleteStartRow({ tableId, row }) {
      if (this.tableId !== tableId) {
        return;
      }
      this.rowClass = (rowData, _) =>
        rowData[this.primaryKeyField] === row[this.primaryKeyField] &&
        `${this.loadingRowClass} danger`;
    },
    async deleteRow({ tableId, row }) {
      if (this.tableId !== tableId) {
        return;
      }
      if (this.isAsync) {
        return this.reloadTable(this.tableId);
      }
      this.dtData = this.renderService.postProcessDeleteRow(this.dtData, row);
      this.total -= 1;
      this.resetRowClass();
      this.emitData();
    },
    removeSelectedRow({ tableId }) {
      if (this.tableId === tableId) {
        this.selected = null;
      }
    },
    removeSelectedSiblingRows({ tableId }) {
      if (this.tableId !== tableId) {
        this.selected = null;
      }
    },
    restoreFailedRow({ tableId, _ }) {
      if (this.tableId !== tableId) {
        return;
      }
      this.resetRowClass();
    },
    startIsLoadingRow({ tableId, row }) {
      if (this.tableId !== tableId) {
        return;
      }
      this.rowClass = (rowData, _) =>
        rowData[this.primaryKeyField] === row[this.primaryKeyField] && `${this.loadingRowClass}`;
    },
    stopIsLoadingRow({ tableId, _ }) {
      if (this.tableId !== tableId) {
        return;
      }
      this.resetRowClass();
    },
    async handleRow(tableId, fn) {
      if (this.tableId !== tableId) {
        return;
      }
      if (this.isAsync) {
        return this.reloadTable(tableId);
      }

      this.isLoading = true;
      try {
        await fn();
      } catch (e) {
        Logger.log(e.message, Logger.LEVEL_ERROR);
      } finally {
        this.isLoading = false;
        this.resetRowClass();
      }
    },
    emitRow(row) {
      EventBus.signal(TableEvent.formatNewRowDataTableId(this.tableId, row.id), {
        row
      });
    },
    emitData() {
      const name = TableEvent.LOAD_DATA_TABLE_ID.replace('{table_id}', this.tableId);
      EventBus.signal(name, {
        data: this.dtData,
        columns: this.dtColumns,
        total: this.total
      });
    },
    canSelectRow() {
      return this.isRowSelectable;
    },
    isRowCheckable() {
      return this.checkable;
    },
    onRowClick(row) {
      if (this.canSelectRow()) {
        this.selected = this.selected?.id === row.id ? null : row;
        this.$emit('selected', this.selected);
        if (this.hasSiblingTables) {
          EventBus.signal(TableEvent.REMOVE_SIBLING_SELECTED_ROWS, this.tableId);
        }
      }
    },
    refreshSelectedRow(row, updatedRow) {
      if (row.id === this.selected.id) {
        this.$emit('selected', updatedRow);
      }
    },
    onCheckClick(...args) {
      const [checkedList, row] = args;
      this.receivedCheckInTick = true;
      this.$nextTick(() => {
        this.receivedCheckInTick = false;
      });
      if (this.isRowCheckable()) {
        this.$emit('on-checked', checkedList, row);
      }
      if (this.isSingleCheckRow) {
        this.checkedRows = [row];
      } else {
        this.checkedRows = checkedList;
      }
    },
    onCheckedRowsUpdate(checkedRows) {
      this.checkedRows = checkedRows;
      if (this.isRowCheckable()) {
        // Prefer emitting from the `check` event when it's available (it includes the toggled row).
        if (!this.receivedCheckInTick) {
          this.$emit('on-checked', checkedRows);
        }
      }
    },
    clearCheckedRows() {
      this.checkedRows = [];
      this.$emit('on-checked', this.checkedRows);
    },
    selectRow({ row }) {
      if (this.canSelectRow()) {
        this.selected = this.dtData.find((data) => data.id === row.id);
        this.$emit('selected', this.selected);
      }
    },
    applyCachedParams() {
      this.page = this.tableParamCache.page;
      this.sortOrder = this.tableParamCache.sortOrder;
      this.defaultSortDirection = this.sortOrder;
      if (this.tableParamCache.sortField) {
        this.sortField = this.tableParamCache.sortField;
      }
      this.defaultSort = [this.sortField, this.sortOrder];
    },
    getValue(column, row) {
      return row[column._field] ?? row[column.field];
    },
    toggleDetails(tableId, row) {
      if (this.tableId !== tableId) {
        return;
      }

      this.$refs.btable?.toggleDetails(row);
    },
    hideDetails(tableId, row) {
      if (this.tableId !== tableId) {
        return;
      }

      if (this.$refs.btable?.isVisibleDetailRow(row)) {
        this.$refs.btable?.toggleDetails(row);
      }
    },
    handleScroll() {
      EventBus.signal(TableEvent.SCROLL);
    },
    hasCustomHeader(column) {
      return column.meta.isCustomHeaderRender || column.meta.headerTooltipConfig;
    },
    highlightRows(tableId, rows) {
      if (this.tableId !== tableId) {
        return;
      }

      this.$nextTick(() => {
        const rowsPrimaryKeys = rows.map((row) => row[this.primaryKeyField]);
        this.rowClass = (rowData, _) =>
          rowsPrimaryKeys.includes(rowData[this.primaryKeyField]) && 'highlighted-row';
      });

      setTimeout(() => {
        this.resetRowClass();
      }, DocumentUIService.getTableRowHighlightedAnimationDurationInMilliseconds());
    }
  },
  created() {
    this.renderService.setTableId(this.tableId);
    this.sortField = this.renderService.getDefaultSort(this.columns);
    this.dtColumns = this.renderService.preProcessColumns(this.columns);
    this.renderService.getPrimaryNameField(this.dtColumns);
    this.renderService.getPrimaryKeyField(this.dtColumns);

    if (this.canCacheParams) {
      this.applyCachedParams();
    }

    if (this.predefinedData) {
      this.$watch('predefinedData', () => {
        this.processData();
      });
      this.processData();
    } else {
      this.isLoading = true;
    }
  },
  mounted() {
    this.loadData();
    this.$el.querySelector('.table-wrapper')?.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    this.$el.querySelector('.table-wrapper')?.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style scoped>
.list-section {
  height: 100%;
  display: flex;
  flex-direction: column;

  &.box {
    padding: var(--ce-15px);
    box-shadow: none;
    border-radius: var(--ce-radius-large);
  }
}

.column-tooltip {
  text-transform: none;
}

:deep(.b-table) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.b-table .table-wrapper) {
  flex-grow: 1;
}
</style>
